import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getSupabaseAdmin } from "@/integrations/external-supabase/client.server";
import { resolveCurrentUserId } from "@/lib/articulos.server";

const Zona = z.object({
  kind: z.enum(["provincia", "ciudad", "distrito", "barrio"]),
  id: z.string().uuid(),
});

const SaveInput = z.object({
  demandaId: z.string().uuid().nullable().optional(),
  operacion: z.string().nullable().optional(),
  tipoLocalIds: z.array(z.string().uuid()).default([]),
  usoLocalIds: z.array(z.string().uuid()).default([]),
  zonas: z.array(Zona).default([]),
  presupuesto: z.number().nullable().optional(),
  rentabilidad: z.number().nullable().optional(),
  superficie: z.number().nullable().optional(),
  salidaHumos: z.boolean().nullable().optional(),
  terraza: z.boolean().nullable().optional(),
  terrazaMesas: z.number().nullable().optional(),
  detalles: z.string().nullable().optional(),
  proyectoMomento: z.string().nullable().optional(),
  pagoEstructura: z.string().nullable().optional(),
  dependenciaOperacion: z.string().nullable().optional(),
  necesidadLocal: z.string().nullable().optional(),
});

export const getMyIncompletaDemanda = createServerFn({ method: "GET" }).handler(async () => {
  const userId = await resolveCurrentUserId();
  if (!userId) return null;
  const sb = getSupabaseAdmin();
  const { data: c } = await sb
    .from("contactos")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();
  if (!c) return null;
  const { data: d } = await sb
    .from("demandas")
    .select("id, updated_at, created_at")
    .eq("contacto_id", c.id)
    .eq("estado", "incompleta")
    .not("visibilidades", "cs", "{off_market}")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return d ?? null;
});

// Devuelve la demanda activa (cualquier estado no cancelado/archivado/completado)
// más reciente del usuario. Usada por /local para asociar solicitudes a la
// demanda del usuario aunque no venga con ?demanda= en la URL.
export const getMyActiveDemandaId = createServerFn({ method: "GET" }).handler(async () => {
  const userId = await resolveCurrentUserId();
  if (!userId) return null;
  const sb = getSupabaseAdmin();
  const { data: c } = await sb
    .from("contactos")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();
  if (!c) return null;
  const { data: d } = await sb
    .from("demandas")
    .select("id")
    .eq("contacto_id", c.id)
    .eq("activa", true)
    .not("visibilidades", "cs", "{off_market}")
    .not("estado", "in", "(cancelada,archivada,completada)")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return d?.id ?? null;
});

// Badge del header: demanda activa más reciente del usuario en estados que
// muestran el contador de coincidencias en verde. El contador refleja las
// coincidencias visibles, restando las que el usuario ha descartado.
export const getMyDemandaBadge = createServerFn({ method: "GET" }).handler(async () => {
  const userId = await resolveCurrentUserId();
  if (!userId) return null;
  const sb = getSupabaseAdmin();
  const { data: c } = await sb
    .from("contactos")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();
  if (!c) return null;
  const { data: d } = await sb
    .from("demandas")
    .select("id, estado, match_count, last_matched_at")
    .eq("contacto_id", c.id)
    .not("visibilidades", "cs", "{off_market}")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!d) return null;
  if (d.estado === "incompleta") return null;

  let matchCount = (d.match_count as number | null) ?? 0;

  // Si ya se ha calculado la caché de coincidencias, descartamos las que el
  // usuario haya marcado como descartadas para que el contador coincida con
  // lo que ve en el listado.
  if (d.last_matched_at) {
    const { data: cacheRows } = await sb
      .from("demanda_matches_cache")
      .select("anuncio_id")
      .eq("demanda_id", d.id);
    const ids = ((cacheRows ?? []) as Array<{ anuncio_id: string }>).map((r) => r.anuncio_id);
    if (ids.length > 0) {
      const { count: discardedCount } = await sb
        .from("anuncio_interacciones")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("descartado", true)
        .in("anuncio_id", ids);
      matchCount = Math.max(0, ids.length - (discardedCount ?? 0));
    } else {
      matchCount = 0;
    }
  }

  return {
    id: d.id as string,
    estado: d.estado as string,
    match_count: matchCount,
  };
});


// Devuelve la demanda con los metadatos relacionados necesarios para
// re-hidratar el formulario del conector (nombres/slugs de tipos, usos y zonas).
export const getConectorDemandaData = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const sb = getSupabaseAdmin();
    const { data: c } = await sb
      .from("contactos")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!c) throw new Error("Contacto no encontrado");

    const { data: dem, error } = await sb
      .from("demandas")
      .select("*")
      .eq("id", data.id)
      .eq("contacto_id", c.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!dem) return null;

    const opIds = (dem.operacion_ids ?? []) as string[];
    const tipoIds = (dem.tipo_local_ids ?? []) as string[];
    const usoIds = (dem.uso_local_ids ?? []) as string[];
    const provIds = (dem.provincia_ids ?? []) as string[];
    const ciuIds = (dem.ciudad_ids ?? []) as string[];
    const disIds = (dem.distrito_ids ?? []) as string[];
    const barIds = (dem.barrio_ids ?? []) as string[];

    const [opsRes, tiposRes, usosRes, provsRes, ciusRes, disRes, barRes] = await Promise.all([
      opIds.length ? sb.from("operaciones").select("id, slug").in("id", opIds) : Promise.resolve({ data: [] as { id: string; slug: string }[] }),
      tipoIds.length ? sb.from("tipos_local").select("id, slug, nombre").in("id", tipoIds) : Promise.resolve({ data: [] as { id: string; slug: string; nombre: string }[] }),
      usoIds.length ? sb.from("usos_local").select("id, slug, nombre, tipo_local_id").in("id", usoIds) : Promise.resolve({ data: [] as { id: string; slug: string; nombre: string; tipo_local_id: string | null }[] }),
      provIds.length ? sb.from("provincias").select("id, slug, nombre").in("id", provIds) : Promise.resolve({ data: [] as { id: string; slug: string; nombre: string }[] }),
      ciuIds.length ? sb.from("ciudades").select("id, slug, nombre, provincia_id").in("id", ciuIds) : Promise.resolve({ data: [] as { id: string; slug: string; nombre: string; provincia_id: string | null }[] }),
      disIds.length ? sb.from("distritos").select("id, slug, nombre, ciudad_id").in("id", disIds) : Promise.resolve({ data: [] as { id: string; slug: string; nombre: string; ciudad_id: string | null }[] }),
      barIds.length ? sb.from("barrios").select("id, slug, nombre, distrito_id").in("id", barIds) : Promise.resolve({ data: [] as { id: string; slug: string; nombre: string; distrito_id: string | null }[] }),
    ]);

    // Nombres de tipos_local para adjuntar a usos
    const tipoNombresById = new Map<string, string>();
    for (const t of tiposRes.data ?? []) tipoNombresById.set(t.id, t.nombre);
    const extraTipoIds = Array.from(
      new Set((usosRes.data ?? []).map((u) => u.tipo_local_id).filter((x): x is string => !!x && !tipoNombresById.has(x))),
    );
    if (extraTipoIds.length) {
      const { data: extra } = await sb.from("tipos_local").select("id, nombre").in("id", extraTipoIds);
      for (const t of extra ?? []) tipoNombresById.set(t.id, t.nombre);
    }

    return {
      id: dem.id as string,
      estado: (dem.estado ?? null) as string | null,
      referencia: (dem.referencia ?? null) as string | null,
      operacionSlug: (opsRes.data ?? [])[0]?.slug ?? null,
      tipos: (tiposRes.data ?? []).map((t) => ({
        kind: "tipo" as const, id: t.id, slug: t.slug, nombre: t.nombre,
      })),
      usos: (usosRes.data ?? []).map((u) => ({
        kind: "uso" as const,
        id: u.id,
        slug: u.slug,
        nombre: u.nombre,
        tipoLocalId: u.tipo_local_id ?? undefined,
        tipoLocalNombre: u.tipo_local_id ? tipoNombresById.get(u.tipo_local_id) : undefined,
      })),
      zonas: [
        ...(provsRes.data ?? []).map((z) => ({ kind: "provincia" as const, id: z.id, slug: z.slug, nombre: z.nombre })),
        ...(ciusRes.data ?? []).map((z) => ({ kind: "ciudad" as const, id: z.id, slug: z.slug, nombre: z.nombre })),
        ...(disRes.data ?? []).map((z) => ({ kind: "distrito" as const, id: z.id, slug: z.slug, nombre: z.nombre })),
        ...(barRes.data ?? []).map((z) => ({ kind: "barrio" as const, id: z.id, slug: z.slug, nombre: z.nombre })),
      ],
      precio_alquiler_min: dem.precio_alquiler_min,
      precio_alquiler_max: dem.precio_alquiler_max,
      precio_traspaso_min: dem.precio_traspaso_min,
      precio_traspaso_max: dem.precio_traspaso_max,
      precio_venta_min: dem.precio_venta_min,
      precio_venta_max: dem.precio_venta_max,
      rent_bruta_min: dem.rent_bruta_min,
      superficie_min: dem.superficie_min,
      mesas_terraza_min: dem.mesas_terraza_min,
      h_terraza: dem.h_terraza,
      h_salida_humos: dem.h_salida_humos,
      notas: dem.notas,
      proyecto_momento: dem.proyecto_momento,
      pago_estructura: dem.pago_estructura,
      dependencia_operacion: dem.dependencia_operacion,
      necesidad_local: dem.necesidad_local,
    };
  });


export const saveConectorDemanda = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => SaveInput.parse(d))
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const sb = getSupabaseAdmin();

    const { data: c } = await sb
      .from("contactos")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!c) throw new Error("Contacto no encontrado");

    // Operación
    const opSlug = (data.operacion ?? "").trim();
    let operacion_ids: string[] = [];
    if (opSlug && opSlug !== "indeciso") {
      const { data: op } = await sb
        .from("operaciones")
        .select("id")
        .eq("slug", opSlug)
        .maybeSingle();
      if (op) operacion_ids = [op.id];
    }

    // Ubicaciones + padres
    const provIds = new Set<string>();
    const ciuIds = new Set<string>();
    const disIds = new Set<string>();
    const barIds = new Set<string>();
    for (const z of data.zonas ?? []) {
      if (z.kind === "provincia") provIds.add(z.id);
      else if (z.kind === "ciudad") ciuIds.add(z.id);
      else if (z.kind === "distrito") disIds.add(z.id);
      else if (z.kind === "barrio") barIds.add(z.id);
    }
    if (barIds.size) {
      const { data: bs } = await sb
        .from("barrios")
        .select("id, distrito_id")
        .in("id", Array.from(barIds));
      (bs ?? []).forEach((b: { distrito_id: string | null }) => {
        if (b.distrito_id) disIds.add(b.distrito_id);
      });
    }
    if (disIds.size) {
      const { data: ds } = await sb
        .from("distritos")
        .select("id, ciudad_id")
        .in("id", Array.from(disIds));
      (ds ?? []).forEach((d: { ciudad_id: string | null }) => {
        if (d.ciudad_id) ciuIds.add(d.ciudad_id);
      });
    }
    if (ciuIds.size) {
      const { data: cs } = await sb
        .from("ciudades")
        .select("id, provincia_id")
        .in("id", Array.from(ciuIds));
      (cs ?? []).forEach((c: { provincia_id: string | null }) => {
        if (c.provincia_id) provIds.add(c.provincia_id);
      });
    }

    const payload: Record<string, unknown> = {
      contacto_id: c.id,
      estado: "incompleta",
      activa: true,
      notificar_coincidencias: true,
      visibilidades: ["publico"],
      origenes: [],
      estado_inmueble: [],
      niveles_transito: [],
      metadata: {
        operacion: data.operacion ?? null,
        tipoLocalIds: data.tipoLocalIds ?? [],
        usoLocalIds: data.usoLocalIds ?? [],
        zonas: data.zonas ?? [],
        presupuesto: data.presupuesto ?? null,
        rentabilidad: data.rentabilidad ?? null,
        superficie: data.superficie ?? null,
        salidaHumos: data.salidaHumos ?? null,
        terraza: data.terraza ?? null,
        terrazaMesas: data.terrazaMesas ?? null,
        detalles: data.detalles ?? null,
        proyectoMomento: data.proyectoMomento ?? null,
        pagoEstructura: data.pagoEstructura ?? null,
        dependenciaOperacion: data.dependenciaOperacion ?? null,
        necesidadLocal: data.necesidadLocal ?? null,
        savedAt: new Date().toISOString(),
      },
      h_salida_humos: data.salidaHumos === true ? true : null,
      h_terraza: data.terraza === true ? true : null,


      notas: data.detalles && data.detalles.trim() ? data.detalles.trim() : null,
      proyecto_momento: data.proyectoMomento?.trim() ? data.proyectoMomento.trim() : null,
      pago_estructura: data.pagoEstructura?.trim() ? data.pagoEstructura.trim() : null,
      dependencia_operacion: data.dependenciaOperacion?.trim() ? data.dependenciaOperacion.trim() : null,
      necesidad_local: data.necesidadLocal?.trim() ? data.necesidadLocal.trim() : null,
      mesas_terraza_min:
        typeof data.terrazaMesas === "number" && data.terrazaMesas > 0
          ? Math.floor(data.terrazaMesas * 0.8)
          : null,
      mesas_terraza_max: null,
      operacion_ids,
      tipo_local_ids: Array.from(new Set(data.tipoLocalIds ?? [])),
      uso_local_ids: Array.from(new Set(data.usoLocalIds ?? [])),
      licencia_ids: [],
      provincia_ids: Array.from(provIds),
      ciudad_ids: Array.from(ciuIds),
      distrito_ids: Array.from(disIds),
      barrio_ids: Array.from(barIds),
      precio_alquiler_min: null,
      precio_alquiler_max: null,
      precio_traspaso_min: null,
      precio_traspaso_max: null,
      precio_venta_min: null,
      precio_venta_max: null,
      rent_bruta_min: null,
      rent_bruta_max: null,
      superficie_min: null,
      superficie_max: null,
    };

    const pres = data.presupuesto;
    if (typeof pres === "number" && pres > 0) {
      const min = Math.round(pres * 0.6);
      const max = Math.round(pres * 1.2);
      if (opSlug === "traspaso") {
        payload.precio_traspaso_min = min;
        payload.precio_traspaso_max = max;
      } else if (opSlug === "alquiler") {
        payload.precio_alquiler_min = min;
        payload.precio_alquiler_max = max;
      } else if (opSlug === "venta" || opSlug === "rentabilidad") {
        payload.precio_venta_min = min;
        payload.precio_venta_max = max;
      }
    }
    const rent = data.rentabilidad;
    if (typeof rent === "number" && rent > 0) {
      payload.rent_bruta_min = Math.round(rent * 0.9 * 100) / 100;
    }
    const sup = data.superficie;
    if (typeof sup === "number" && sup > 0) {
      payload.superficie_min = Math.round(sup * 0.9);
    }

    if (data.demandaId) {
      const { error } = await sb
        .from("demandas")
        .update(payload)
        .eq("id", data.demandaId)
        .eq("contacto_id", c.id);
      if (error) throw new Error(error.message);
      return { id: data.demandaId };
    }

    // Cancelar demandas previas (activas) del mismo contacto antes de crear la nueva.
    // Las demandas VIP (off-market, creadas desde /conector-vip) van por separado
    // y NO se cancelan.
    const { data: prev } = await sb
      .from("demandas")
      .select("id, notas_internas")
      .eq("contacto_id", c.id)
      .not("visibilidades", "cs", "{off_market}")
      .not("estado", "in", "(cancelada,archivada,completada)");

    for (const p of prev ?? []) {
      const marca = "#Cancelada al crearse una nueva demanda.";
      const notas = p.notas_internas && p.notas_internas.trim()
        ? `${p.notas_internas}\n${marca}`
        : marca;
      await sb
        .from("demandas")
        .update({ estado: "cancelada", activa: false, notas_internas: notas })
        .eq("id", p.id);
    }

    const { data: row, error } = await sb
      .from("demandas")
      .insert({ ...payload, created_by: userId, origen: "conector" })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

// Marca una demanda del conector como iniciada cuando el usuario llega al
// paso final (enriquecimiento con IA + conteo de coincidencias completado).
export const completarConectorDemanda = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ demandaId: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const sb = getSupabaseAdmin();

    const { data: c } = await sb
      .from("contactos")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!c) throw new Error("Contacto no encontrado");

    const { data: updated, error } = await sb
      .from("demandas")
      .update({ estado: "iniciada" })
      .eq("id", data.demandaId)
      .eq("contacto_id", c.id)
      .eq("estado", "incompleta")
      .select("id")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (updated) {
      try {
        const { recomputeDemandaMatchesInternal } = await import("@/lib/demandas.server");
        await recomputeDemandaMatchesInternal(data.demandaId);
      } catch (e) {
        console.error("completarConectorDemanda recompute", e);
      }
    }
    return { ok: true };
  });
